import * as React from 'react'

/**
 * Keeps open dialogs inside the part of the screen the on-screen keyboard leaves visible.
 *
 * iOS Safari shrinks only window.visualViewport when the keyboard opens; vh, dvh and fixed positioning keep
 * measuring the whole screen, so a dialog's lower fields end up behind the keyboard. While any dialog is open,
 * this writes the visible area to three variables on the root that DialogContent sizes and places itself by:
 * --visual-viewport-top, --visual-viewport-bottom and --visual-viewport-height.
 */
const VARIABLES = ['--visual-viewport-top', '--visual-viewport-bottom', '--visual-viewport-height']

let openDialogs = 0

function syncVisibleArea() {
  const viewport = window.visualViewport
  if (!viewport) return
  const root = document.documentElement
  const bottom = Math.max(0, root.clientHeight - viewport.offsetTop - viewport.height)
  root.style.setProperty('--visual-viewport-top', `${viewport.offsetTop}px`)
  root.style.setProperty('--visual-viewport-bottom', `${bottom}px`)
  root.style.setProperty('--visual-viewport-height', `${viewport.height}px`)
  // The keyboard opens after the field has focus, so bring that field back into view once the dialog shrinks.
  const focused = document.activeElement
  if (focused instanceof HTMLElement && focused.closest('[data-slot="dialog-content"]')) {
    focused.scrollIntoView({ block: 'nearest' })
  }
}

/** Tracks the visible area for as long as the calling dialog is mounted. */
export function useVisualViewport() {
  React.useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    if (openDialogs++ === 0) {
      syncVisibleArea()
      viewport.addEventListener('resize', syncVisibleArea)
      viewport.addEventListener('scroll', syncVisibleArea)
    }
    return () => {
      if (--openDialogs > 0) return
      viewport.removeEventListener('resize', syncVisibleArea)
      viewport.removeEventListener('scroll', syncVisibleArea)
      for (const name of VARIABLES) document.documentElement.style.removeProperty(name)
    }
  }, [])
}
